/**
 * [B] 세차 차량 기록 — 외주(gtli) 관리자 "세차 차량 기록"(세차 인식 이벤트) 재현.
 *
 * 컬럼(벤더 순서): 차량출입시각·구분·위치·차량번호·상품명·하부세차·세차횟수·메모·사진.
 * loop_event_time 그룹핑(대표행)은 백엔드. 사진은 인증 blob 프록시로 토글 노출(공개 URL 없음, 저장 없음).
 * 데이터: GET /owner-console/modernwash/vendor-parity/wash-log (live read-only).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  LinearProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import QueryError from '../../components/QueryError';
import { getAdminRole } from '../../utils/auth';
import { getVendorWashLog, getVendorWashImage } from '../../api/modernwashVendor';
import {
  surfaceSx,
  kstDate,
  formatVendorDateTime,
  SourceBanner,
  shouldRenderBody,
  useMasterStores,
  useColumnSort,
  DateField,
  TextField,
  StoreSelect,
} from './vendorParityCommon';

const COLUMNS = [
  { key: 'loopEventTime', label: '차량출입시각', fmt: formatVendorDateTime },
  { key: 'type', label: '구분', align: 'center' },
  { key: 'location', label: '위치' },
  { key: 'licensePlate', label: '차량번호', mono: true },
  { key: 'productionName', label: '상품명' },
  { key: 'underbodyWash', label: '하부세차', align: 'center' },
  { key: 'washCount', label: '세차횟수', align: 'right' },
  { key: 'contents', label: '메모' },
];

const SortHeader = ({ col, sort, onSort }) => (
  <TableCell
    align={col.align || 'left'}
    onClick={() => onSort(col.key)}
    sx={{ whiteSpace: 'nowrap', fontWeight: 700, cursor: 'pointer', userSelect: 'none' }}
  >
    {col.label}
    {sort.key === col.key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
  </TableCell>
);

const cellValue = (row, col) => {
  const v = row[col.key];
  if (col.fmt) return col.fmt(v);
  if (v == null || v === '') return '';
  return String(v);
};

/**
 * 사진 셀 — 토글 시 각 이미지를 인증 blob 으로 받아 objectURL 로 렌더. 접기/언마운트 시 revoke.
 * (공개 <img src> 금지 · Bearer 게이트 유지 · 이미지 저장 안 함)
 */
const ImageCell = ({ images, storeId }) => {
  const [open, setOpen] = useState(false);
  const [urls, setUrls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const urlsRef = useRef([]);
  // 진행 중 로드 식별자 — 로드 중 닫기/재토글 시 뒤늦게 온 응답을 버려 objectURL 누수를 막는다.
  const reqRef = useRef(0);

  const revoke = useCallback(() => {
    urlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    urlsRef.current = [];
    setUrls([]);
  }, []);

  useEffect(() => () => revoke(), [revoke]);

  const toggle = async () => {
    if (open) {
      setOpen(false);
      reqRef.current += 1; // 진행 중 로드 무효화
      revoke();
      return;
    }
    setOpen(true);
    if (!images || images.length === 0) return;
    const myReq = (reqRef.current += 1);
    // 새 로드 전에 남아있는 objectURL 을 먼저 revoke(덮어쓰기 누수 방지).
    urlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    urlsRef.current = [];
    setLoading(true);
    setFailed(false);
    try {
      const blobs = await Promise.all(
        images.map((img) => getVendorWashImage({ folder: img.folder, fname: img.fname, storeId })),
      );
      const objectUrls = blobs.map((b) => URL.createObjectURL(b));
      if (reqRef.current !== myReq) {
        // 로드 중 닫혔거나 재토글됨 — 새로 만든 URL 을 즉시 폐기(화면 반영 안 함).
        objectUrls.forEach((u) => URL.revokeObjectURL(u));
        return;
      }
      urlsRef.current = objectUrls;
      setUrls(objectUrls);
    } catch {
      if (reqRef.current === myReq) setFailed(true);
    } finally {
      if (reqRef.current === myReq) setLoading(false);
    }
  };

  const count = images?.length || 0;
  return (
    <Box>
      <Box
        component="button"
        type="button"
        onClick={toggle}
        disabled={count === 0}
        sx={{
          height: 28,
          px: 1.25,
          borderRadius: '6px',
          border: '1px solid rgba(0,0,0,0.16)',
          backgroundColor: count === 0 ? '#F1F5F9' : '#fff',
          color: count === 0 ? 'text.disabled' : 'text.primary',
          fontSize: 12,
          fontWeight: 700,
          cursor: count === 0 ? 'default' : 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        {open ? '닫기' : `보기${count ? ` (${count})` : ''}`}
      </Box>
      {open ? (
        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {loading ? (
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>불러오는 중…</Typography>
          ) : failed ? (
            <Typography sx={{ fontSize: 12, color: 'error.main' }}>이미지를 불러오지 못했습니다.</Typography>
          ) : (
            urls.map((u, i) => (
              <Box
                key={u}
                component="img"
                src={u}
                alt={`캡처 ${i + 1}`}
                sx={{ width: 180, maxWidth: '100%', borderRadius: '8px', border: '1px solid', borderColor: 'divider' }}
              />
            ))
          )}
        </Box>
      ) : null}
    </Box>
  );
};

const VendorWashLogPage = () => {
  const navigate = useNavigate();
  const role = getAdminRole();
  const isMaster = role === 'master';
  const today = kstDate(0);

  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [lpInput, setLpInput] = useState('');
  const [targetLp, setTargetLp] = useState('');
  const rangeInvalid = startDate > endDate;

  const stores = useMasterStores(isMaster);
  const [storeId, setStoreId] = useState('');
  const storeParam = isMaster ? storeId || undefined : undefined;

  const [state, setState] = useState({ loading: true, error: null, res: null });
  const load = useCallback(() => {
    if (rangeInvalid) return undefined;
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    getVendorWashLog({ storeId: storeParam, start: startDate, end: endDate, targetLp: targetLp || undefined })
      .then((res) => alive && setState({ loading: false, error: null, res }))
      .catch((e) => alive && setState({ loading: false, error: e, res: null }));
    return () => {
      alive = false;
    };
  }, [storeParam, startDate, endDate, targetLp, rangeInvalid]);
  useEffect(() => load(), [load]);

  const res = state.res;
  const items = res?.items || [];
  const { sorted, sort, toggle } = useColumnSort(items);
  const showBody = shouldRenderBody(res) && !state.loading && !state.error;

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, sm: 3, lg: 4 }, py: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <IconButton size="small" onClick={() => navigate('/modernwash')} aria-label="ModernWash 운영으로">
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: { xs: 20, md: 28 }, fontWeight: 800, letterSpacing: '-0.03em' }}>
            세차 차량 기록
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            외주 관리자 세차 인식 이벤트 · 사진은 클릭 시 로드
          </Typography>
        </Box>
        <Box sx={{ ml: { md: 'auto' }, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            size="small"
            label={isMaster ? '전체 매장 조회 (master)' : '본인 매장 고정 (manager)'}
            variant="outlined"
            sx={{ height: 30, borderRadius: 999, fontWeight: 700 }}
          />
          <IconButton size="small" onClick={load} aria-label="새로고침">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <SourceBanner res={res} />
      </Box>

      <Paper variant="outlined" sx={{ ...surfaceSx, p: { xs: 1.75, md: 2 }, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <DateField label="시작일" value={startDate} max={endDate} onChange={setStartDate} />
          <DateField label="종료일" value={endDate} min={startDate} max={today} onChange={setEndDate} />
          <TextField
            label="차량번호"
            value={lpInput}
            placeholder="예: 12가3456"
            onChange={setLpInput}
            onEnter={() => setTargetLp(lpInput.trim())}
          />
          <Box
            component="button"
            type="button"
            onClick={() => setTargetLp(lpInput.trim())}
            sx={{
              height: 34,
              px: 2,
              borderRadius: '8px',
              border: 'none',
              backgroundColor: '#7C3AED',
              color: '#fff',
              fontWeight: 700,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            검색
          </Box>
          {isMaster ? <StoreSelect stores={stores} value={storeId} onChange={setStoreId} /> : null}
        </Box>
        {rangeInvalid ? (
          <Typography sx={{ fontSize: 12.5, color: 'error.main', mt: 1 }}>시작일이 종료일보다 늦습니다.</Typography>
        ) : null}
      </Paper>

      {state.error ? (
        <QueryError error={state.error} onRetry={load} context="세차 차량 기록" />
      ) : state.loading ? (
        <LinearProgress sx={{ mb: 2 }} />
      ) : null}

      {showBody ? (
        <Paper variant="outlined" sx={{ ...surfaceSx, p: { xs: 1.5, md: 2 } }}>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 1.5 }}>
            총 {items.length.toLocaleString()}건 · 헤더 클릭 시 정렬
          </Typography>
          {items.length === 0 ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>기간 내 세차 기록이 없습니다.</Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 620 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {COLUMNS.map((col) => (
                      <SortHeader key={col.key} col={col} sort={sort} onSort={toggle} />
                    ))}
                    <TableCell sx={{ fontWeight: 700 }}>사진</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sorted.map((row, i) => (
                    <TableRow key={`${row.loopEventTime}-${row.licensePlate}-${i}`} hover>
                      {COLUMNS.map((col) => (
                        <TableCell
                          key={col.key}
                          align={col.align || 'left'}
                          sx={{ whiteSpace: 'nowrap', fontFamily: col.mono ? 'monospace' : 'inherit' }}
                        >
                          {cellValue(row, col)}
                        </TableCell>
                      ))}
                      <TableCell sx={{ minWidth: 90 }}>
                        <ImageCell images={row.images} storeId={storeParam} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      ) : null}
    </Box>
  );
};

export default VendorWashLogPage;
